import { AppShell, Burger, Group, NavLink, ScrollArea, Stack, Text } from '@mantine/core';
import { useDisclosure, useDocumentTitle } from '@mantine/hooks';
import { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { Logo } from './Logo';
import { AVAILABLE_ROUTES, NAV_SECTIONS, NavItem } from './nav';
import { NotificationBell } from './NotificationBell';
import { UserMenu } from './UserMenu';

function isActive(pathname: string, to: string): boolean {
  return to === '/internal' ? pathname === '/internal' : pathname === to || pathname.startsWith(`${to}/`);
}

export function AppLayout() {
  useDocumentTitle('MExP — Makin Laboratories');
  const [opened, { toggle, close }] = useDisclosure(false);
  const { pathname } = useLocation();
  const { can, hasRole } = useAuth();

  useEffect(() => close(), [pathname, close]);

  const visible = (item: NavItem) =>
    AVAILABLE_ROUTES.has(item.to) && (!item.module || can(item.module, item.action ?? 'view')) && (!item.roles || hasRole(...item.roles));

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 256, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding={{ base: 'md', sm: 'lg' }}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Toggle navigation" />
            <Link to="/internal" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Logo />
            </Link>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <NotificationBell />
            <UserMenu />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap="md">
            {NAV_SECTIONS.map((section) => {
              const items = section.items.filter(visible);
              if (!items.length) return null;
              return (
                <Stack key={section.label} gap={2}>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="sm" pb={4} lts={0.4}>
                    {section.label}
                  </Text>
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      component={Link}
                      to={item.to}
                      label={item.label}
                      leftSection={<item.icon size={18} stroke={1.7} />}
                      active={isActive(pathname, item.to)}
                      variant="light"
                      style={{ borderRadius: 'var(--mantine-radius-md)' }}
                    />
                  ))}
                </Stack>
              );
            })}
          </Stack>
        </AppShell.Section>
        <AppShell.Section>
          <Text fz={11} c="dimmed" px="sm" pt="sm">
            MExP · Makin Laboratories Pvt. Ltd.
          </Text>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
